import { RankXPName } from "./rank";
import { InputValues } from "../types/PromotionValues";

/** First editable rank index (Apprentice). */
export const PROMOTION_FIRST_RANK_INDEX = 1;

/** Last editable rank index (Godfather). */
export const PROMOTION_LAST_RANK_INDEX = RankXPName.length - 1;

/** Promotion object key for a rank index (0 = Nobody, 1 = Apprentice, …). */
export const promotionRankKey = (rankIndex: number): string =>
  RankXPName[rankIndex]?.toLowerCase().replace(/\s+/g, "") ?? `rank${rankIndex}`;

/** Display label for a promotion key (e.g. "Capo bastone"). */
export const promotionKeyToLabel = (key: string): string => {
  const idx = promotionKeyToRankNumber(key);
  return idx >= 0 ? RankXPName[idx] : key;
};

/** Rank index for a promotion object key; -1 if unknown. */
export const promotionKeyToRankNumber = (key: string): number => {
  const normalized = key.toLowerCase().replace(/\s+/g, "");
  return RankXPName.findIndex(
    (name) => name.toLowerCase().replace(/\s+/g, "") === normalized
  );
};

/** Keys shown in manage / detail UI: Apprentice → Godfather (rank indices 1–29). */
export const PROMOTION_EDITABLE_KEYS = RankXPName.slice(
  PROMOTION_FIRST_RANK_INDEX,
  RankXPName.length
).map((_, i) => promotionRankKey(i + PROMOTION_FIRST_RANK_INDEX));

/** Nobody + all playable ranks (30 keys). */
export const PROMOTION_OBJECT_KEY_COUNT = RankXPName.length;

export const PROMOTION_UI_COLUMN_COUNT = 3;

/** Split promotion keys into columns for the manage / detail layout. */
export const getPromotionKeysForColumn = (columnIndex: number): string[] => {
  const perColumn = Math.ceil(
    PROMOTION_EDITABLE_KEYS.length / PROMOTION_UI_COLUMN_COUNT
  );
  const start = columnIndex * perColumn;
  return PROMOTION_EDITABLE_KEYS.slice(start, start + perColumn);
};

export function contractRewardsToPromotionObject(
  rewardCashAmounts: number[]
): Record<string, number> {
  const promotion: Record<string, number> = {};
  promotion[promotionRankKey(0)] = 0;
  for (let i = 0; i < rewardCashAmounts.length; i++) {
    promotion[promotionRankKey(i + 1)] = Math.floor(rewardCashAmounts[i] ?? 0);
  }
  return promotion;
}

export function promotionValuesToSetPayload(values: InputValues): {
  ranks: number[];
  amountsCash: number[];
} {
  const ranks: number[] = [];
  const amountsCash: number[] = [];

  PROMOTION_EDITABLE_KEYS.forEach((key) => {
    if (!(key in values)) return;
    const rank = promotionKeyToRankNumber(key);
    if (rank < 1) return;
    ranks.push(rank);
    amountsCash.push(Math.floor(Number(values[key]) || 0));
  });

  return { ranks, amountsCash };
}

export function hasPromotionValuesChanged(
  initial: InputValues | undefined,
  current: InputValues | undefined
): boolean {
  if (!initial || !current) return false;
  return PROMOTION_EDITABLE_KEYS.some(
    (key) =>
      Math.floor(Number(initial[key]) || 0) !== Math.floor(Number(current[key]) || 0)
  );
}
